import { Helmet } from "react-helmet-async";
import { CANONICAL_ORIGIN, canonicalUrl } from "@/lib/app-config";

/** WebPage subtypes we use across the site. */
export type WebPageType =
  | "WebPage"
  | "AboutPage"
  | "ContactPage"
  | "CollectionPage"
  | "CheckoutPage"
  | "MedicalWebPage";

export interface WebPageJsonLdProps {
  /** Site-relative path, e.g. "/privacy" */
  path: string;
  /** Page name — mirror the visible H1 / <title> subject */
  name: string;
  description: string;
  /** Schema.org type, defaults to WebPage */
  type?: WebPageType;
  /** Optional breadcrumb trail name for the page's primary section */
  primaryImage?: string;
  /** ISO date (YYYY-MM-DD) of last meaningful content update */
  dateModified?: string;
  /** Extra properties merged into the node (e.g. mainEntity, significantLink) */
  extra?: Record<string, unknown>;
}

const absolute = (url: string) =>
  url.startsWith("http") ? url : `${CANONICAL_ORIGIN}${url.startsWith("/") ? url : `/${url}`}`;

/**
 * Emits a schema.org WebPage (or subtype) node for a route, keyed by the
 * page's canonical URL and linked to the sitewide WebSite / Organization
 * entities declared in index.html.
 *
 * mainEntityOfPage self-references the page so crawlers can tell which entity
 * the page is primarily about.
 */
export const WebPageJsonLd = ({
  path,
  name,
  description,
  type = "WebPage",
  primaryImage = "/og-image.jpg",
  dateModified,
  extra,
}: WebPageJsonLdProps) => {
  const url = canonicalUrl(path);

  const data = {
    "@context": "https://schema.org",
    "@type": type,
    "@id": url,
    url,
    name,
    description,
    inLanguage: "en-US",
    mainEntityOfPage: { "@id": url },
    isPartOf: { "@id": `${CANONICAL_ORIGIN}/#website` },
    publisher: { "@id": `${CANONICAL_ORIGIN}/#organization` },
    primaryImageOfPage: {
      "@type": "ImageObject",
      url: absolute(primaryImage),
      width: 1200,
      height: 630,
    },
    ...(dateModified ? { dateModified } : {}),
    ...(extra ?? {}),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
};

export default WebPageJsonLd;
